import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, Lock, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../store/useAuthStore';
import { useStorageStore } from '../store/useStorageStore';
import { useTranslation } from 'react-i18next';
import { trackEvent, trackGeneralEvent } from '../lib/analytics';
import { LATEST_TOU_VERSION, LATEST_PRIVACY_VERSION } from '../lib/policyVersions';

export const TermsUpdateModal: React.FC = () => {
  const { t } = useTranslation();
  const { 
    touUpdateRequired, 
    privacyUpdateRequired, 
    acceptLatestTerms, 
    user 
  } = useAuthStore();

  const {
    anonymousTouUpdateRequired,
    anonymousPrivacyUpdateRequired,
    anonymousTouVersion,
    anonymousPrivacyVersion,
    setAnonymousConsentVersions,
  } = useStorageStore();

  // Use the appropriate flags depending on auth state.
  // Signed-in users: checked against backend-stored version.
  // Anonymous users: checked against localStorage-stored version.
  const effectiveTouRequired    = user ? touUpdateRequired    : anonymousTouUpdateRequired;
  const effectivePrivacyRequired = user ? privacyUpdateRequired : anonymousPrivacyUpdateRequired;
  
  const [touAgreed, setTouAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const hasTrackedPrompt = useRef(false);

  useEffect(() => {
    if ((effectiveTouRequired || effectivePrivacyRequired) && !hasTrackedPrompt.current) {
      hasTrackedPrompt.current = true;
      trackEvent('terms_reconsent_prompted', {
        user_type: user ? 'signed_in' : 'anonymous',
        tou_required: effectiveTouRequired,
        privacy_required: effectivePrivacyRequired
      });
    }
  }, [effectiveTouRequired, effectivePrivacyRequired, user]);

  // Render nothing if no updates are required for this user
  if (!effectiveTouRequired && !effectivePrivacyRequired) {
    return null;
  }

  // Submit button is active only when all required, outdated documents have checkboxes checked
  const allRequiredChecked = 
    (!effectiveTouRequired || touAgreed) && 
    (!effectivePrivacyRequired || privacyAgreed);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allRequiredChecked || submitting) return;

    setSubmitError(null);
    setSubmitting(true);
    try {
      if (user) {
        // Signed-in: persist accepted versions to the backend
        await acceptLatestTerms(
          touUpdateRequired ? touAgreed : false, 
          privacyUpdateRequired ? privacyAgreed : false
        );
      } else {
        // Anonymous: persist accepted versions to localStorage
        setAnonymousConsentVersions(
          anonymousTouUpdateRequired ? LATEST_TOU_VERSION : (anonymousTouVersion ?? LATEST_TOU_VERSION),
          anonymousPrivacyUpdateRequired ? LATEST_PRIVACY_VERSION : (anonymousPrivacyVersion ?? LATEST_PRIVACY_VERSION)
        );
      }
      trackEvent('terms_reconsent_accepted', {
        user_type: user ? 'signed_in' : 'anonymous',
        tou_accepted: effectiveTouRequired ? touAgreed : false,
        privacy_accepted: effectivePrivacyRequired ? privacyAgreed : false
      });
    } catch (err: any) {
      console.error('Failed to submit re-consent:', err);
      setSubmitError(err?.message || 'Failed to submit agreement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoToTerms = () => {
    trackGeneralEvent('tou_opened', { source: 'reconsent_modal' });
    window.open('/termsofuse', '_blank');
  };

  const handleGoToPrivacy = () => {
    trackGeneralEvent('privacy_opened', { source: 'reconsent_modal' });
    window.open('/privacy', '_blank');
  };


  return (
    <div className="terms-update-overlay">
      <div className="terms-update-panel glass-panel">
        
        {/* Header Section */}
        <div style={{ textAlign: 'center' }}>
          <div className="terms-badge">
            <ShieldCheck size={14} />
            <span>{t('terms_update.badge', { defaultValue: 'Important Update' })}</span>
          </div>
          <h1 className="terms-heading">
            {effectiveTouRequired && effectivePrivacyRequired
              ? t('terms_update.title_both', { defaultValue: "We've Updated Our Policies" })
              : effectiveTouRequired
              ? t('terms_update.title_tou', { defaultValue: "We've Updated Our Terms of Use" })
              : t('terms_update.title_privacy', { defaultValue: "We've Updated Our Privacy Policy" })
            }
          </h1>
          <p className="terms-subheading">
            {effectiveTouRequired && effectivePrivacyRequired
              ? t('terms_update.subtitle_both', { defaultValue: 'Please review and accept the latest changes to our Terms of Use and Privacy Policy to continue.' })
              : effectiveTouRequired
              ? t('terms_update.subtitle_tou', { defaultValue: 'Please review and accept the latest changes to our Terms of Use to continue.' })
              : t('terms_update.subtitle_privacy', { defaultValue: 'Please review and acknowledge the latest changes to our Privacy Policy to continue.' })
            }
          </p>
        </div>

        {/* Submit Error */}
        {submitError && (
          <div className="terms-error-banner">
            <AlertCircle size={16} />
            <span>{submitError}</span>
          </div>
        )}

        {/* Action Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          
          {/* Conditional Terms Checkbox */}
          {effectiveTouRequired && (
            <label className="terms-checkbox-label">
              <input
                type="checkbox"
                checked={touAgreed}
                onChange={(e) => setTouAgreed(e.target.checked)}
                disabled={submitting}
                className="terms-custom-checkbox"
              />
              <span className="terms-checkbox-text-content">
                {t('terms_update.checkbox_tou_prefix', { defaultValue: 'I have read and agree to the updated ' })}
                <button 
                  type="button" 
                  onClick={(e) => { e.stopPropagation(); handleGoToTerms(); }} 
                  className="terms-inline-link"
                >
                  {t('splash.terms_of_use', { defaultValue: 'Terms of Use' })}
                </button>
              </span>
            </label>
          )}

          {/* Conditional Privacy Checkbox */}
          {effectivePrivacyRequired && (
            <label className="terms-checkbox-label">
              <input
                type="checkbox"
                checked={privacyAgreed}
                onChange={(e) => setPrivacyAgreed(e.target.checked)}
                disabled={submitting}
                className="terms-custom-checkbox"
              />
              <span className="terms-checkbox-text-content">
                {t('terms_update.checkbox_privacy_prefix', { defaultValue: 'I have read and acknowledge the updated ' })}
                <button 
                  type="button" 
                  onClick={(e) => { e.stopPropagation(); handleGoToPrivacy(); }} 
                  className="terms-inline-link"
                >
                  {t('splash.privacy_policy', { defaultValue: 'Privacy Policy' })}
                </button>
              </span>
            </label>
          )}

          {/* Submit Action Button */}
          <button
            type="submit"
            disabled={!allRequiredChecked || submitting}
            className={`terms-submit-btn ${!allRequiredChecked ? 'disabled' : ''}`}
            style={{ marginTop: 8 }}
          >
            {submitting ? (
              <span>{t('terms_update.processing', { defaultValue: 'Processing...' })}</span>
            ) : allRequiredChecked ? (
              <>
                <ShieldCheck size={16} />
                <span>{t('terms_update.accept_btn', { defaultValue: 'Accept & Continue' })}</span>
              </>
            ) : (
              <>
                <Lock size={16} />
                <span>{t('terms_update.accept_btn_locked', { defaultValue: 'Please Accept to Continue' })}</span>
              </>
            )}
          </button>
        </form>

      </div>
    </div>
  );
};
